import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface Roster {
  id: string;
  code: string;
  name: string;
  effective_from: string;
  recurrence_days: number;
  number_of_groups: number;
}
interface Shift {
  id: string;
  code: string;
  name: string;
}
const EMPTY = { code: '', name: '', effectiveFrom: '', recurrenceDays: 7, numberOfGroups: 1 };

export default function DutyRosters() {
  const [rosters, setRosters] = useState<Roster[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<Roster | null>(null);
  const [error, setError] = useState('');

  const [scheduleTarget, setScheduleTarget] = useState<Roster | null>(null);
  const [cells, setCells] = useState<Record<string, string>>({});

  const load = async () => {
    const [r, s] = await Promise.all([api.get('/tms/duty-rosters'), api.get('/tms/shifts')]);
    setRosters(r.data.rosters || []);
    setShifts(s.data.shifts || []);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setError('');
    setShowForm(true);
  };

  const openEdit = (row: Roster) => {
    setEditingId(row.id);
    setForm({
      code: row.code,
      name: row.name,
      effectiveFrom: row.effective_from ? row.effective_from.slice(0, 10) : '',
      recurrenceDays: row.recurrence_days,
      numberOfGroups: row.number_of_groups,
    });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) await api.put(`/tms/duty-rosters/${editingId}`, form);
      else await api.post('/tms/duty-rosters', form);
      setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/tms/duty-rosters/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  const openSchedule = async (row: Roster) => {
    setScheduleTarget(row);
    const res = await api.get(`/tms/duty-rosters/${row.id}/schedule`);
    const map: Record<string, string> = {};
    for (const s of res.data.schedules || []) {
      map[`${s.group_number}-${s.day_of_week}`] = s.shift_id || '';
    }
    setCells(map);
  };

  const setCell = async (group: number, day: number, shiftId: string) => {
    setCells((prev) => ({ ...prev, [`${group}-${day}`]: shiftId }));
    if (!scheduleTarget) return;
    await api.post(`/tms/duty-rosters/${scheduleTarget.id}/schedule`, { groupNumber: group, dayOfWeek: day, shiftId: shiftId || null });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Duty Roster</h1>
        <Button onClick={openCreate}>+ New</Button>
      </div>
      <Table
        columns={[
          { key: 'code', header: 'Code' },
          { key: 'name', header: 'Name' },
          { key: 'recurrence_days', header: 'Recurrence (days)' },
          { key: 'number_of_groups', header: 'Groups' },
          {
            key: 'actions',
            header: 'Actions',
            render: (row) => (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => openEdit(row)}>
                  Edit
                </Button>
                <Button variant="ghost" onClick={() => openSchedule(row)}>
                  Schedule Grid
                </Button>
                <Button variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(row)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]}
        rows={rosters}
      />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Duty Roster' : 'New Duty Roster'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input label="Name" maxLength={50} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Effective From" type="date" value={form.effectiveFrom} onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} />
          <Input
            label="Recurrence Days (max 42)"
            type="number"
            max={42}
            value={form.recurrenceDays}
            onChange={(e) => setForm({ ...form, recurrenceDays: Number(e.target.value) })}
          />
          <Input
            label="Number of Groups (max 6)"
            type="number"
            max={6}
            min={1}
            value={form.numberOfGroups}
            onChange={(e) => setForm({ ...form, numberOfGroups: Number(e.target.value) })}
          />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!scheduleTarget} onClose={() => setScheduleTarget(null)} title={`Schedule Grid — ${scheduleTarget?.name}`}>
        <div className="overflow-x-auto">
          <table className="text-sm">
            <thead>
              <tr>
                <th className="p-1 text-left">Group</th>
                {DAY_NAMES.map((d) => (
                  <th key={d} className="p-1 text-left">
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: scheduleTarget?.number_of_groups || 1 }, (_, g) => g + 1).map((group) => (
                <tr key={group}>
                  <td className="p-1 font-medium">Group {group}</td>
                  {Array.from({ length: 7 }, (_, i) => i + 1).map((day) => (
                    <td key={day} className="p-1">
                      <select
                        className="border rounded px-1 py-0.5 text-xs"
                        value={cells[`${group}-${day}`] || ''}
                        onChange={(e) => setCell(group, day, e.target.value)}
                      >
                        <option value="">—</option>
                        {shifts.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.code}
                          </option>
                        ))}
                      </select>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        message={`Delete duty roster "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
